import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft, Download, Eye, FileText, AlertCircle, MessageCircle, Printer, Upload, AlertTriangle, X, Pencil, Activity } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import STLViewer from "@/components/STLViewer";
import CaseChat from "@/components/CaseChat";
import CaseActivity from "@/components/CaseActivity";
import CaseEditModal from "@/components/CaseEditModal";
import DesignApprovalPanel from "@/components/DesignApprovalPanel";
import { recordRecent } from "@/components/RecentItems";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIEWABLE = new Set(["stl", "ply", "obj"]);

// Heuristic: guess upper / lower from filename (same logic as STLViewer).
function guessJaw(filename = "") {
    const f = filename.toLowerCase();
    if (/(upper|maxilla|max|top)/.test(f)) return "upper";
    if (/(lower|mandible|mand|bottom)/.test(f)) return "lower";
    return null;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-GB", { hour12: false }); } catch { return iso; }
}

function StatusPill({ status }) {
    const map = {
        received: "bg-slate-100 text-slate-700",
        accepted: "bg-cyan-50 text-cyan-800",
        in_design: "bg-indigo-50 text-indigo-800",
        manufacturing: "bg-violet-50 text-violet-800",
        out_for_delivery: "bg-amber-50 text-amber-800",
        delivered: "bg-emerald-50 text-emerald-800",
    };
    return (
        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium ${map[status] || "bg-slate-100 text-slate-700"}`}>
            {status.replace(/_/g, " ")}
        </span>
    );
}

export default function CaseDetail() {
    const { caseId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewer, setViewer] = useState(null); // { url, filename }
    const [reuploadingFor, setReuploadingFor] = useState(null); // filename being repaired
    // Receiving-checklist missing-items flag flow. Only lab staff can flag.
    const [flagModal, setFlagModal] = useState(false);
    const [flagItems, setFlagItems] = useState({});  // {material: true/false}
    const [flagNote, setFlagNote] = useState("");
    const [flagging, setFlagging] = useState(false);
    const isLabStaff = ["technician", "lead_technician", "employee", "superadmin"].includes(user?.role || "");
    // Edit-case modal (Lead-tech / employee / superadmin only — gated by `manage_cases`)
    const [editing, setEditing] = useState(false);
    const canEditCase = ["lead_technician", "employee", "superadmin"].includes(user?.role || "");
    // Right-side panel tab: "chat" or "activity"
    const [rightTab, setRightTab] = useState("chat");
    const reuploadInputRef = useRef(null);

    const fileUrl = (id) => `${API}/files/${id}`;

    const triggerReupload = (filename) => {
        setReuploadingFor(filename);
        reuploadInputRef.current?.click();
    };

    const onReuploadPicked = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        const targetName = reuploadingFor;
        setReuploadingFor(null);
        if (!file || !targetName) return;
        const toastId = toast.loading(`Re-uploading ${targetName}…`);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data: up } = await axios.post(`${API}/files/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            await axios.post(`${API}/cases/${caseId}/files/attach`, { files: [{ id: up.id, name: targetName, size: up.size, type: up.type }] });
            toast.success(`${targetName} re-attached`, { id: toastId });
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Re-upload failed", { id: toastId });
        }
    };

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/cases/${caseId}`);
            setData(data);
            recordRecent(data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Case not available");
        } finally {
            setLoading(false);
        }
    }, [caseId]);

    useEffect(() => {
        if (!user) {
            navigate("/login");
            return;
        }
        load();
    }, [load, user, navigate]);

    if (loading) {
        return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;
    }
    if (error || !data) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-5">
                <div className="text-center">
                    <AlertCircle className="mx-auto text-rose-500 mb-3" size={28} />
                    <div className="font-display text-xl text-slate-900">{error || "Case not found"}</div>
                    <Link to={user?.role === "client" ? "/portal" : "/admin"} className="mt-4 inline-block text-cyan-700 underline text-[13.5px]">← Back to dashboard</Link>
                </div>
            </div>
        );
    }

    const backHref = user.role === "client" ? "/portal" : "/admin";
    return (
        <div data-testid="case-detail" className="max-w-[1200px] mx-auto px-5 py-8">
            <div className="mb-6">
                <Link to={backHref} className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900">
                    <ArrowLeft size={14} /> {user.role === "client" ? "My cases" : "Lab control"}
                </Link>
            </div>

            {/* Header card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 mb-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500">· Case</div>
                        <h1 className="font-display text-3xl tracking-tight text-slate-900 mt-1" data-testid="case-number">{data.case_number}</h1>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <StatusPill status={data.status} />
                            {data.is_late && <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 text-[11px] font-medium">LATE</span>}
                            {data.is_backlog && <span data-testid="case-backlog-chip" className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[11px] font-medium">BACKLOG</span>}
                            <span className="text-[12.5px] text-slate-500">Submitted {fmtDate(data.created_at)}</span>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {data.case_number && (
                            <Link to={`/track/${data.case_number}`} target="_blank" className="tr-btn-ghost !py-2 text-[12.5px]"><Eye size={14} /> Public tracker</Link>
                        )}
                        {canEditCase && (
                            <button
                                onClick={() => setEditing(true)}
                                data-testid="open-case-edit"
                                className="tr-btn !py-2 text-[12.5px]"
                                style={{ background: "#0f172a", color: "white" }}
                            >
                                <Pencil size={14} /> Edit case
                            </button>
                        )}
                        {user.role === "employee" && (
                            <a
                                href={`${API}/cases/${data.id}/label`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="print-label-btn"
                                className="tr-btn !py-2 text-[12.5px] bg-cyan-700 hover:bg-cyan-800"
                                style={{ background: "#0e7490", color: "white" }}
                            >
                                <Printer size={14} /> Print label
                            </a>
                        )}
                        {data.case_number && data.status === "delivered" && (
                            <a href={`${API}/cases/${data.case_number}/warranty.pdf`} target="_blank" rel="noreferrer" className="tr-btn-ghost !py-2 text-[12.5px]"><Download size={14} /> Warranty PDF</a>
                        )}
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-100">
                    <Cell k="Patient" v={data.patient_id} />
                    <Cell k="Product" v={data.product} />
                    <Cell k="Shade" v={data.shade || "—"} />
                    <Cell k="Teeth" v={(data.tooth_numbers && data.tooth_numbers.length) ? data.tooth_numbers.join(", ") : "—"} />
                    <Cell k="Clinic" v={data.clinic_name} />
                    <Cell k="Dentist" v={data.dentist_name} />
                    <Cell k="Email" v={data.email} />
                    <Cell k="Phone" v={data.phone || "—"} />
                    {data.specifications && data.specifications.arch && <Cell k="Arch" v={data.specifications.arch} />}
                    {data.specifications && (data.specifications.span_from || data.specifications.span_to) && (
                        <Cell k="Span" v={`${data.specifications.span_from} → ${data.specifications.span_to}`} />
                    )}
                    {data.specifications && data.specifications.material && <Cell k="Material" v={data.specifications.material} />}
                    <Cell k="Technician" v={data.assigned_to_name || "Unassigned"} />
                    {data.order_date && (
                        <Cell k="Order date" v={data.order_date} testid="case-detail-order-date" />
                    )}
                    {data.expected_delivery && (
                        <Cell k="Expected delivery" v={data.expected_delivery} testid="case-detail-expected-delivery" />
                    )}
                    {data.purchase_order_number && (
                        <Cell k="Clinic PO #" v={data.purchase_order_number} testid="case-detail-po-number" />
                    )}
                </div>
                {Array.isArray(data.attached_materials) && data.attached_materials.length > 0 && (
                    <div id="materials" className="mt-4 rounded-xl border border-cyan-200 bg-cyan-50/50 px-4 py-3" data-testid="case-detail-attached-materials">
                        <div className="flex items-center justify-between gap-3 mb-1.5">
                            <div className="text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-cyan-800">
                                Attached physical materials
                            </div>
                            {isLabStaff && data.materials_missing_at && (
                                <span className="text-[10.5px] font-mono-tr tracking-[0.18em] uppercase text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200" data-testid="materials-missing-flag">
                                    Items reported missing
                                </span>
                            )}
                            {isLabStaff && !data.materials_missing_at && (
                                <button
                                    onClick={() => {
                                        const initial = {};
                                        data.attached_materials.forEach((m) => { initial[m] = false; });
                                        setFlagItems(initial);
                                        setFlagNote("");
                                        setFlagModal(true);
                                    }}
                                    data-testid="flag-missing-btn"
                                    className="inline-flex items-center gap-1 text-[11.5px] font-medium text-rose-700 hover:bg-rose-100 px-2 py-1 rounded-md transition"
                                    title="Tap if any item didn't arrive in the courier bag"
                                >
                                    <AlertTriangle size={11} /> Flag missing
                                </button>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {data.attached_materials.map((m) => (
                                <span key={m} className="px-2.5 py-1 rounded-full bg-white border border-cyan-200 text-cyan-900 text-[12px]">{m}</span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Flag-missing modal — visible only to lab staff. */}
                {flagModal && (
                    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-5" data-testid="flag-missing-modal">
                            <div className="bg-white rounded-2xl w-full max-w-md p-6 max-h-[80vh] overflow-y-auto tr-modal-card relative">
                            <button onClick={() => setFlagModal(false)} className="absolute top-4 end-4 text-slate-400 hover:text-slate-700" aria-label="Close"><X size={18} /></button>
                            <div className="flex items-center gap-2 text-rose-700">
                                <AlertTriangle size={18} />
                                <div className="tr-overline text-rose-800">· Receiving discrepancy</div>
                            </div>
                            <div className="font-display text-xl text-slate-900 tracking-tight mt-1">Which items didn&apos;t arrive?</div>
                            <p className="text-[13px] text-slate-600 mt-2 leading-relaxed">
                                Tick everything that&apos;s missing or damaged. The clinic + orders inbox will be emailed, and the case chat gets a system note for the audit trail.
                            </p>
                            <div className="mt-4 space-y-2 max-h-56 overflow-y-auto pr-1">
                                {(data.attached_materials || []).map((m) => (
                                    <label key={m} className="flex items-center gap-2.5 rounded-lg border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50">
                                        <input
                                            type="checkbox"
                                            checked={!!flagItems[m]}
                                            onChange={(e) => setFlagItems((s) => ({ ...s, [m]: e.target.checked }))}
                                            data-testid={`flag-item-${m.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                            className="h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-300"
                                        />
                                        <span className="text-[13px] text-slate-800">{m}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="mt-4">
                                <label htmlFor="flag-note" className="block text-[10.5px] font-mono-tr tracking-[0.16em] uppercase text-slate-500 mb-1">Note (optional)</label>
                                <textarea
                                    id="flag-note"
                                    rows={3}
                                    value={flagNote}
                                    onChange={(e) => setFlagNote(e.target.value)}
                                    data-testid="flag-note"
                                    placeholder="e.g. PVS impression arrived but the lower was warped — clinic to send a fresh one."
                                    className="w-full rounded-lg border border-slate-300 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 px-3 py-2 text-[13.5px] outline-none"
                                />
                            </div>
                            <div className="mt-5 flex justify-end gap-2">
                                <button onClick={() => setFlagModal(false)} className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-[13px]" data-testid="flag-cancel">
                                    Cancel
                                </button>
                                <button
                                    onClick={async () => {
                                        const missing = Object.entries(flagItems).filter(([, v]) => v).map(([k]) => k);
                                        if (missing.length === 0) {
                                            toast.error("Tick at least one item.");
                                            return;
                                        }
                                        setFlagging(true);
                                        try {
                                            await axios.post(`${API}/cases/${caseId}/materials/flag-missing`, {
                                                missing_items: missing,
                                                note: flagNote.trim() || null,
                                            });
                                            toast.success("Clinic notified · audit logged");
                                            setFlagModal(false);
                                            // Refresh the case so the badge updates
                                            const { data: refreshed } = await axios.get(`${API}/cases/${caseId}`);
                                            setData(refreshed);
                                        } catch (err) {
                                            toast.error(err?.response?.data?.detail || "Could not flag missing items");
                                        } finally { setFlagging(false); }
                                    }}
                                    disabled={flagging || Object.values(flagItems).every((v) => !v)}
                                    data-testid="flag-confirm"
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-700 hover:bg-rose-800 text-white text-[13px] font-medium disabled:opacity-50"
                                >
                                    {flagging ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
                                    Flag &amp; notify clinic
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {data.notes && (
                    <div className="mt-5 pt-5 border-t border-slate-100">
                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-1.5">Clinical notes</div>
                        <p className="text-[14px] text-slate-800 whitespace-pre-wrap leading-relaxed">{data.notes}</p>
                    </div>
                )}
            </div>

            {/* File viewer + chat side-by-side on lg */}
            <div className="grid lg:grid-cols-5 gap-5">
                {/* Files / 3D viewer */}
                <div className="lg:col-span-3 space-y-4">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-display text-[16px] tracking-tight text-slate-900">Case files</h3>
                            <span className="text-[11.5px] text-slate-500 font-mono-tr">{data.files?.length || 0} file(s)</span>
                        </div>

                        {/* BITE CTA — surfaces only when ≥2 viewable meshes exist + scanner exported
                            shared spatial coordinates (we infer from upper/lower in the filename). */}
                        {(() => {
                            const viewable = (data.files || []).filter((f) => f.id && VIEWABLE.has((f.name || "").split(".").pop().toLowerCase()));
                            if (viewable.length < 2) return null;
                            const jaws = viewable.map((f) => ({ ...f, jaw: guessJaw(f.name) }));
                            const hasBothJaws = jaws.some((j) => j.jaw === "upper") && jaws.some((j) => j.jaw === "lower");
                            return (
                                <button
                                    onClick={() => setViewer({
                                        meshes: jaws.map((j) => ({ url: fileUrl(j.id), filename: j.name, jaw: j.jaw, label: j.name })),
                                        filename: hasBothJaws ? "Bite (upper + lower)" : `Combined view · ${viewable.length} meshes`,
                                    })}
                                    data-testid="view-bite-btn"
                                    className="w-full mb-3 px-3.5 py-2.5 rounded-xl border border-cyan-300 bg-gradient-to-r from-cyan-50 to-teal-50 text-cyan-900 text-[12.5px] font-medium hover:from-cyan-100 inline-flex items-center justify-center gap-2"
                                >
                                    <Eye size={13} /> {hasBothJaws ? "View bite (upper + lower in occlusion)" : `Open all ${viewable.length} meshes together`}
                                </button>
                            );
                        })()}

                        {(!data.files || data.files.length === 0) ? (
                            <div className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center text-[13.5px] text-slate-500">
                                No files attached to this case.
                            </div>
                        ) : (
                            <div className="space-y-2" data-testid="case-files">
                                {data.files.map((f, idx) => {
                                    const ext = (f.name || "").split(".").pop().toLowerCase();
                                    const canView = f.id && VIEWABLE.has(ext);
                                    return (
                                        <div key={f.id || `${f.name}-${idx}`} className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg border border-slate-200 bg-slate-50/40">
                                            <FileText size={15} className="text-slate-500 shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[13.5px] text-slate-900 truncate font-medium">{f.name}</div>
                                                <div className="text-[11px] text-slate-500 font-mono-tr">{(f.size / 1024).toFixed(0)} KB · .{ext}</div>
                                            </div>
                                            {canView && (
                                                <button
                                                    data-testid={`view-file-${f.id}`}
                                                    onClick={() => setViewer({ url: fileUrl(f.id), filename: f.name })}
                                                    className="px-2.5 py-1 rounded-lg border border-cyan-200 bg-cyan-50 text-cyan-800 text-[11.5px] font-medium hover:bg-cyan-100 inline-flex items-center gap-1"
                                                >
                                                    <Eye size={12} /> View 3D
                                                </button>
                                            )}
                                            {f.id ? (
                                                <a
                                                    href={fileUrl(f.id)}
                                                    download={f.name}
                                                    data-testid={`download-file-${f.id}`}
                                                    className="px-2.5 py-1 rounded-lg border border-slate-200 text-slate-700 text-[11.5px] font-medium hover:bg-slate-100 inline-flex items-center gap-1"
                                                >
                                                    <Download size={12} /> Download
                                                </a>
                                            ) : (
                                                <button
                                                    onClick={() => triggerReupload(f.name)}
                                                    data-testid={`reupload-file-${idx}`}
                                                    className="px-2.5 py-1 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-[11.5px] font-medium hover:bg-amber-100 inline-flex items-center gap-1"
                                                    title="The original upload was lost — pick the file again to restore"
                                                >
                                                    <Upload size={12} /> Re-upload
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {viewer && (
                        <STLViewer
                            url={viewer.url}
                            filename={viewer.filename}
                            meshes={viewer.meshes}
                            onClose={() => setViewer(null)}
                        />
                    )}
                    <input ref={reuploadInputRef} type="file" accept=".stl,.ply,.obj,.dcm,.zip" className="hidden" onChange={onReuploadPicked} data-testid="reupload-file-input" />

                    {/* Finished design — tech upload + clinic approval */}
                    <DesignApprovalPanel
                        case={data}
                        role={user.role === "client" ? "clinic" : "tech"}
                        onView={(f) => setViewer({ url: fileUrl(f.id), filename: f.name })}
                        onCaseRefresh={load}
                    />
                </div>

                {/* Right column · Chat ↔ Activity tabs */}
                <div className="lg:col-span-2 space-y-3">
                    <div className="rounded-xl border border-slate-200 bg-white flex overflow-hidden" role="tablist" data-testid="case-right-tabs">
                        <button
                            role="tab"
                            onClick={() => setRightTab("chat")}
                            data-testid="tab-chat"
                            className={`flex-1 px-4 py-2.5 text-[12.5px] font-medium inline-flex items-center justify-center gap-1.5 transition ${rightTab === "chat" ? "text-cyan-700 bg-cyan-50/60" : "text-slate-500 hover:text-slate-800"}`}
                        >
                            <MessageCircle size={13} /> Chat
                        </button>
                        <button
                            role="tab"
                            onClick={() => setRightTab("activity")}
                            data-testid="tab-activity"
                            className={`flex-1 px-4 py-2.5 text-[12.5px] font-medium inline-flex items-center justify-center gap-1.5 transition border-l border-slate-100 ${rightTab === "activity" ? "text-cyan-700 bg-cyan-50/60" : "text-slate-500 hover:text-slate-800"}`}
                        >
                            <Activity size={13} /> Activity
                        </button>
                    </div>
                    {rightTab === "chat" ? (
                        <>
                            <CaseChat caseId={caseId} myEmail={user?.email} myRole={user?.role} />
                            <div className="text-[11px] text-slate-500 px-1 flex items-center gap-1.5">
                                <MessageCircle size={11} /> All discussion is logged immutably for clinical compliance.
                            </div>
                        </>
                    ) : (
                        <CaseActivity caseId={caseId} />
                    )}
                </div>
            </div>

            {editing && (
                <CaseEditModal
                    caseData={data}
                    onClose={() => setEditing(false)}
                    onSaved={(updated) => { setEditing(false); setData(updated); }}
                />
            )}
        </div>
    );
}

function Cell({ k, v, testid }) {
    return (
        <div className="rounded-lg border border-slate-100 bg-slate-50/40 px-3.5 py-2.5" data-testid={testid}>
            <div className="font-mono-tr text-[10px] tracking-[0.2em] uppercase text-slate-500">{k}</div>
            <div className="mt-0.5 text-[13.5px] text-slate-900 font-medium break-words">{v}</div>
        </div>
    );
}
